import BaseModel from "lib/model";
import Entries from "collections/catalogs/entries";
import Meta from "models/meta";
import Restrictions from "collections/catalogs/restrictions";
import Links from "collections/catalogs/links";
import moment from "moment";
import { CatalogProcessingStatuses } from "components/import-linesheet/import-linesheet-types";

export default BaseModel.extend({
    idAttribute: "id",
    url: function () {
        if (this.isNew()) {
            return "/api/v3.1/catalogs";
        }
        return `/api/v3.1/catalog/${this.id}`;
    },
    props: {
        id: "objectId",
        active: { type: "boolean", default: true, required: true },
        archived: { type: "boolean", default: true, required: true },

        brand_id: "objectId",
        owner_account_id: "objectId",

        folder: { type: "string" },

        title: { type: "string" },
        description: { type: "string" },
        cover: { type: "string" },
        portrait_image: { type: "string" },

        default_pdf_template: { type: "string", default: "" },

        type: {
            type: "string",
            values: ["linesheet", "custom-list"],
            required: true,
        },
        delivery_window_start: { type: "utcDate" },
        delivery_window_end: { type: "utcDate" },
        prebook: { type: "boolean" },
        hide_when_expired: { type: "boolean" },
        visible_to_guest_buyers: { type: "boolean", default: true },
        created_on: { type: "utcDate" },
        modified_on: { type: "utcDate" },
        available_from: { type: "number" },
        __out_of_range: { type: "boolean" },
        processing: { type: "number" },
        es_synced_on: { type: "string" },
    },
    children: {
        __meta: Meta,
    },
    collections: {
        entries: Entries,
        restrictions: Restrictions,
        links: Links,
    },
    derived: {
        _link: {
            cache: true,
            deps: ["id", "type"],
            fn() {
                const type = this.type === "linesheet" ? "linesheet" : "list";
                return `/brand/${this.brand_id}/${type}/${this.id}`;
            },
        },
        _parentLink: {
            cache: true,
            deps: ["id", "type"],
            fn() {
                const type = this.type === "linesheet" ? "linesheets" : "lists";
                return `/brand/${this.brand_id}/${type}`;
            },
        },
        _coverThumb: {
            cache: false,
            deps: ["cover"],
            fn() {
                if (this.cover) {
                    const [name, ext = "jpg"] = this.cover.split(".");
                    return `//cdn1.nuorder.com/catalog_cover/${name}T.${ext}`;
                }
                return undefined;
            },
        },
        _cover: {
            cache: false,
            deps: ["cover"],
            fn() {
                if (this.cover) {
                    const [name, ext = "jpg"] = this.cover.split(".");
                    return `//cdn1.nuorder.com/catalog_cover/${name}.${ext}`;
                }
                return undefined;
            },
        },
        _portraitImage: {
            cache: false,
            deps: ["portrait_image"],
            fn() {
                if (this.portrait_image) {
                    const [name, ext = "jpg"] = this.portrait_image.split(".");
                    return `//cdn1.nuorder.com/catalog_portrait_image/${name}.${ext}`;
                }
                return undefined;
            },
        },
        _editable: {
            cache: false,
            fn() {
                return this.__meta.__actions.get("__edit") ? true : false;
            },
        },
        _defaultPdfTemplate: {
            cache: false,
            fn() {
                return this.default_pdf_template ? this.default_pdf_template : "landscape_single_16";
            },
        },
        _getCatalogDisplayType: {
            cache: true,
            deps: ["type"],
            fn() {
                return this.type === "linesheet" ? "Linesheet" : "List";
            },
        },
    },
    //namePrefix is leading copy before the name. (Active) -- to indicate the current catalog is the user's active catalog
    getTruncatedName(namePrefix) {
        const name = namePrefix ? `${namePrefix} ${this.title}` : this.title;
        if (name.length > 70) {
            return `${name.substring(0, 70)}...`;
        } else {
            return name;
        }
    },

    _deliveryWindowLabel() {
        let label;
        if (this.delivery_window_start && this.delivery_window_end) {
            const start = moment.utc(this.delivery_window_start).format("MMM DD, YYYY");
            const end = moment.utc(this.delivery_window_end).format("MMM DD, YYYY");
            label = `${start} - ${end}`;
        }

        return label;
    },

    toJSON() {
        const rawData = this.serialize();
        rawData.delivery_window_start =
            rawData.delivery_window_start && rawData.delivery_window_start.startOf("day").valueOf();
        rawData.delivery_window_end = rawData.delivery_window_end && rawData.delivery_window_end.endOf("day").valueOf();
        return rawData;
    },

    isExpired(minimumDeliveryWindow) {
        if (this.delivery_window_start && this.delivery_window_end && minimumDeliveryWindow) {
            const cutOffDate = moment.utc().endOf("day").add(minimumDeliveryWindow, "days").valueOf();

            // Calculate
            const unixEnd = Number(this.delivery_window_end);

            return cutOffDate > unixEnd;
        } else {
            return false;
        }
    },

    isProcessing() {
        return this.processing === CatalogProcessingStatuses.ProductUpload;
    },
});
